/**
 * Order types for persistent survey storage.
 */
import { z } from 'zod';

/**
 * Order API error response type.
 * Used for consistent error responses across order endpoints.
 */
export interface OrderApiError {
  error: string;
  details?: unknown;
}

/**
 * Possible order status values.
 * - INIT: Order created but no survey data saved yet
 * - STARTED: First survey question has been answered
 * - PAYMENT_PENDING: Checkout session created, awaiting payment
 * - PAYMENT_COMPLETED: Payment received via webhook
 * - PAYMENT_FAILED: Payment failed or session expired
 * - GENERATING: Story generation in progress
 * - PLAN_REVIEW: Waiting for human approval of story plan
 * - CHARACTER_SHEET_REVIEW: Waiting for human approval of character sheet
 * - GENERATION_COMPLETE: Generation pipeline finished successfully
 * - GENERATION_FAILED: Generation pipeline encountered an error
 * - PREVIEW_READY: Preview available for customer
 * - CANCELLED: User requested to start over
 * - PRINT_JOB_SUBMITTED: Print job sent to Lulu
 * - SHIPPED: Lulu reports shipment with tracking
 */
export const ORDER_STATUS = [
  'INIT',
  'STARTED',
  'PAYMENT_PENDING',
  'PAYMENT_COMPLETED',
  'PAYMENT_FAILED',
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'GENERATION_COMPLETE',
  'GENERATION_FAILED',
  'PREVIEW_READY',
  'CANCELLED',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
] as const;
export type OrderStatus = (typeof ORDER_STATUS)[number];

/**
 * Public order data (excludes secret).
 */
export const orderPublicSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  surveyJson: z.record(z.string(), z.unknown()).nullable(),
  status: z.enum(ORDER_STATUS),
  paidProductType: z.string().nullable().optional(),
  appEnv: z.string().nullable().optional(),
  aiCostCents: z.number().int().nullable().optional(),
  luluPrintCostCents: z.number().int().nullable().optional(),
  luluShipCostCents: z.number().int().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type OrderPublic = z.infer<typeof orderPublicSchema>;

/**
 * Response from POST /api/orders/init
 */
export const initOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(ORDER_STATUS),
  /** Cookie max age in milliseconds - client should track expiration */
  maxAgeMs: z.number(),
  /**
   * Session ID returned in response body as a cross-origin fallback.
   * In cross-origin deployments (e.g. Vercel frontend + Render backend),
   * the session_id cookie set via Set-Cookie is stored on the API domain
   * and is NOT readable via document.cookie on the frontend domain.
   * The client uses this field to populate localStorage so that the
   * session persists across page navigations (e.g. Stripe redirect).
   */
  sessionId: z.string().uuid(),
});

export type InitOrderResponse = z.infer<typeof initOrderResponseSchema>;

/**
 * Request body for PUT /api/orders/save/:id
 */
export const saveOrderRequestSchema = z.object({
  surveyJson: z.record(z.string(), z.unknown()),
  email: z.string().email().optional(),
});

export type SaveOrderRequest = z.infer<typeof saveOrderRequestSchema>;

/**
 * Response from PUT /api/orders/save/:id
 */
export const saveOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(ORDER_STATUS),
  surveyJson: z.record(z.string(), z.unknown()).nullable(),
});

export type SaveOrderResponse = z.infer<typeof saveOrderResponseSchema>;

/**
 * Response from PUT /api/orders/cancel/:id
 */
export const cancelOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.literal('CANCELLED'),
});

export type CancelOrderResponse = z.infer<typeof cancelOrderResponseSchema>;

// ============================================================================
// Payment Types
// ============================================================================

/**
 * Stripe address from checkout session
 */
export interface StripeAddress {
  city: string | null;
  country: string | null;
  line1: string | null;
  line2: string | null;
  postal_code: string | null;
  state: string | null;
  name?: string | null;
}

/**
 * Response from POST /api/orders/:id/checkout-session
 */
export const checkoutSessionResponseSchema = z.object({
  clientSecret: z.string(),
});

export type CheckoutSessionResponse = z.infer<
  typeof checkoutSessionResponseSchema
>;

/**
 * Response from GET /api/orders/:id/payment-status
 */
export const paymentStatusResponseSchema = z.object({
  status: z.enum(ORDER_STATUS),
  paymentStatus: z.string().nullable(),
  isPaymentComplete: z.boolean(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  amountTotal: z.number().nullable().optional(),
});

export type PaymentStatusResponse = z.infer<typeof paymentStatusResponseSchema>;

/**
 * Check if an order status indicates payment has been completed.
 * Statuses after PAYMENT_COMPLETED all imply the order has been paid for.
 */
export function isPaymentCompleteFromStatus(status: OrderStatus): boolean {
  return (
    [
      'PAYMENT_COMPLETED',
      'GENERATING',
      'GENERATING_PLAN',
      'GENERATING_CHARACTER_SHEET',
      'GENERATING_FINAL',
      'PLAN_REVIEW',
      'CHARACTER_SHEET_REVIEW',
      'GENERATION_COMPLETE',
      'GENERATION_FAILED',
      'PREVIEW_READY',
      'PRINT_JOB_SUBMITTED',
      'SHIPPED',
    ] as readonly string[]
  ).includes(status);
}

// ============================================================================
// Session Types
// ============================================================================

/**
 * Response from GET /api/sessions/active-order
 * Tells the client what to do with an existing session.
 */
export interface ActiveOrderResponse {
  action: 'redirect_finalize' | 'restore_draft' | 'create_new';
  orderId?: string;
  orderStatus?: OrderStatus;
}

/**
 * Response from PUT /api/orders/reset/:id
 * Confirms the order was reset to PAYMENT_COMPLETED.
 */
export interface ResetOrderResponse {
  id: string;
  status: 'PAYMENT_COMPLETED';
}

/**
 * Request body for POST /api/sessions/recover
 */
export interface SessionRecoverRequest {
  email: string;
}

/**
 * Request body for POST /api/sessions/verify-code
 */
export interface SessionVerifyCodeRequest {
  email: string;
  code: string;
}
